import { Users, Eye, Clock, ArrowDownUp, TrendingUp, TrendingDown } from 'lucide-react';
import clsx from 'clsx';
import { formatNumber, formatDurationMs, formatPercent, formatPercentChange } from '../../utils/formatters';
import type { DashboardStats } from '../../api/client';

interface OverviewCardsProps {
  stats?: DashboardStats;
}

export default function OverviewCards({ stats }: OverviewCardsProps) {
  const cards = [
    {
      label: 'Total Sessions',
      value: stats ? formatNumber(stats.totalSessions) : '0',
      change: stats?.sessionsChange ?? 0,
      icon: Eye,
      color: 'text-brand-600 bg-brand-50',
    },
    {
      label: 'Unique Visitors',
      value: stats ? formatNumber(stats.uniqueVisitors) : '0',
      change: stats?.visitorsChange ?? 0,
      icon: Users,
      color: 'text-emerald-600 bg-emerald-50',
    },
    {
      label: 'Avg. Duration',
      value: stats ? formatDurationMs(stats.avgSessionDuration ?? stats.avgDuration ?? 0) : '0s',
      change: stats?.durationChange ?? 0,
      icon: Clock,
      color: 'text-blue-600 bg-blue-50',
    },
    {
      label: 'Bounce Rate',
      value: stats ? formatPercent(stats.bounceRate) : '0%',
      change: stats?.bounceRateChange ?? 0,
      icon: ArrowDownUp,
      color: 'text-orange-600 bg-orange-50',
      invertChange: true,
    },
  ];

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {cards.map((card) => {
        const Icon = card.icon;
        const isPositive = card.invertChange ? card.change < 0 : card.change > 0;
        const isNeutral = card.change === 0;

        return (
          <div key={card.label} className="card">
            <div className="flex items-center justify-between">
              <div className={clsx('rounded-lg p-2', card.color)}>
                <Icon className="h-5 w-5" />
              </div>
              {!isNeutral && (
                <div
                  className={clsx(
                    'flex items-center gap-1 text-xs font-medium',
                    isPositive ? 'text-emerald-600' : 'text-red-600',
                  )}
                >
                  {isPositive ? (
                    <TrendingUp className="h-3 w-3" />
                  ) : (
                    <TrendingDown className="h-3 w-3" />
                  )}
                  {formatPercentChange(card.change)}
                </div>
              )}
            </div>
            <div className="mt-3">
              <p className="text-2xl font-bold text-gray-900">{card.value}</p>
              <p className="text-sm text-gray-500">{card.label}</p>
            </div>
          </div>
        );
      })}
    </div>
  );
}
